import { useRef, useMemo } from 'react';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ChartOptions } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import ChartBox from '../../modules/dashboard/components/ChartBox';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

interface ChartJsBarChartProps {
  data: Array<{ x: string; value: string | number; [key: string]: any }>;
  title: string;
  dataKey?: string;
  height?: number;
  fill?: string;
  stroke?: string;
  yAxisLabel?: string;
  tooltipLabel?: string;
  showGrid?: boolean;
  customTooltipFormatter?: (value: any, name: string, props: any) => [string, string];
  customTickFormatter?: (value: any) => string;
  reverseYAxis?: boolean;
  innerHeader?: React.ReactNode;
}

const ChartJsBarChart = ({
  data,
  title,
  dataKey = 'value',
  height = 350,
  fill = '#000',
  stroke = '#000',
  yAxisLabel,
  tooltipLabel,
  showGrid = true,
  customTooltipFormatter,
  customTickFormatter,
  reverseYAxis = false,
  innerHeader
}: ChartJsBarChartProps) => {
  const chartRef = useRef<ChartJS<'bar', number[], string>>(null);

  // Calculate dynamic left padding based on longest Y-axis label
  const calculateDynamicPadding = () => {
    if (data.length === 0) return 60;

    // Get all values and find the longest formatted string
    const allValues = data.map((item) => Math.abs(Number(item[dataKey]) || 0));
    const maxValue = Math.max(...allValues);
    const minValue = Math.min(...allValues);

    // Test various values that might appear on Y-axis
    const testValues = [maxValue, minValue, maxValue * 0.8, maxValue * 1.2, Math.ceil(maxValue / 1000) * 1000, Math.ceil(maxValue / 100) * 100].filter((val) => val > 0);

    // Format all test values
    const formattedStrings = testValues.map((val) => (customTickFormatter ? customTickFormatter(val) : val.toLocaleString('en-US')));

    // Find the longest string
    const maxStringLength = Math.max(...formattedStrings.map((str) => str.length));
    const longestString = formattedStrings.reduce((a, b) => (a.length > b.length ? a : b));

    // Calculate padding: base 60px + 8px per character + extra padding for Y-axis label
    const basePadding = 60;
    const charWidth = 8;
    const yAxisLabelPadding = yAxisLabel ? 40 : 0;

    const calculatedPadding = basePadding + maxStringLength * charWidth + yAxisLabelPadding;

    console.log('Chart.js Dynamic Padding:', {
      maxValue,
      maxStringLength,
      longestString,
      calculatedPadding
    });

    return Math.min(calculatedPadding, 200); // Cap at 200px to prevent excessive padding
  };

  const chartData = useMemo(
    () => ({
      labels: data.map((item) => item.x),
      datasets: [
        {
          label: tooltipLabel || 'Value',
          data: data.map((item) => Number(item[dataKey]) || 0),
          backgroundColor: fill,
          borderColor: stroke,
          borderWidth: 1,
          borderRadius: 4,
          borderSkipped: false
        }
      ]
    }),
    [data, dataKey, fill, stroke, tooltipLabel]
  );

  const options: ChartOptions<'bar'> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'index',
        intersect: false,
      },
      layout: {
        padding: {
          // left: calculateDynamicPadding(),
          right: 20,
          top: 20,
          bottom: 20
        }
      },
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          enabled: true,
          position: 'nearest',
          backgroundColor: '#ffffff',
          titleColor: '#333333',
          bodyColor: '#666666',
          borderColor: '#d0d0d0',
          borderWidth: 1,
          cornerRadius: 8,
          shadowOffsetX: 2,
          shadowOffsetY: 2,
          shadowBlur: 8,
          shadowColor: 'rgba(0, 0, 0, 0.1)',
          displayColors: false,
          padding: 12,
          boxPadding: 6,
          usePointStyle: false,
          caretSize: 6,
          caretPadding: 8,
          titleFont: {
            size: 13,
            weight: 'bold'
          },
          bodyFont: {
            size: 12,
            weight: 'normal'
          },
          callbacks: {
            label: (context) => {
              const value = context.parsed.y;
              const dataIndex = context.dataIndex;
              const dataPoint = data[dataIndex];

              if (customTooltipFormatter) {
                const [formattedValue, label] = customTooltipFormatter(value, '', { payload: dataPoint });
                return `${label}: ${formattedValue}`;
              }

              const formattedValue = customTickFormatter ? customTickFormatter(value) : value.toLocaleString('en-US');
              return `${tooltipLabel || 'Value'}: ${formattedValue}`;
            }
          }
        }
      },
      scales: {
        x: {
          grid: {
            display: showGrid,
            color: '#f0f0f0'
          },
          ticks: {
            color: '#666',
            font: {
              size: 12
            }
          },
          border: {
            color: '#e0e0e0'
          }
        },
        y: {
          reverse: reverseYAxis,
          grid: {
            display: showGrid,
            color: '#f0f0f0'
          },
          ticks: {
            color: '#666',
            font: {
              size: 12
            },
            callback: function (value) {
              return customTickFormatter ? customTickFormatter(Number(value)) : Number(value).toLocaleString('en-US');
            }
          },
          border: {
            color: '#e0e0e0'
          },
          title: {
            display: !!yAxisLabel,
            text: yAxisLabel,
            color: '#666',
            font: {
              size: 12
            }
          }
        }
      }
    }),
    [data, showGrid, customTickFormatter, yAxisLabel, customTooltipFormatter, tooltipLabel, reverseYAxis]
  );

  return (
    <ChartBox title={title}>
      <div className='f j-e'>
        {innerHeader}
      </div>
      <div style={{ height: `${height}px`, position: 'relative', width: '100%' }}>
        <Bar ref={chartRef} data={chartData} options={options} />
      </div>
    </ChartBox>
  );
};

export default ChartJsBarChart;